import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Person,
  Logout,
  Close,
  SupervisorAccount,
  Home,
} from '@mui/icons-material';
import { permissionService, Permissions } from '../services/permissionService';

const AUTH_SIDEBAR_WIDTH = 300;

interface MainPageSideBarProps {
  open: boolean;
  onClose: () => void;
  onSelectAuth: (authType: 'profile' | 'logout' | 'usermanagement' | 'backtomain') => void;
  currentView?: string;
}

const MainPageSideBar: React.FC<MainPageSideBarProps> = ({ open, onClose, onSelectAuth, currentView }) => {
  
  // Ellenőrizzük, hogy van-e jogosultsága a felhasználó kezeléshez
  const hasUserManagementPermission = permissionService.hasAnyPermission([
    Permissions.ViewUsers,
    Permissions.ViewRoles,
    Permissions.ManageUserRoles
  ]);
  
  // Dinamikus menü a current view alapján
  const getMenuItems = () => {
    if (currentView === 'usermanagement') {
      return [
        { text: 'Vissza a főoldalra', icon: <Home />, key: 'backtomain' },
        { text: 'Profil', icon: <Person />, key: 'profile' },
        { text: 'Kijelentkezés', icon: <Logout />, key: 'logout' },
      ];
    } else {
      const items = [
        { text: 'Profil', icon: <Person />, key: 'profile' }
      ];
      
      // Csak akkor jelenítjük meg a felhasználók kezelése menüpontot, ha van jogosultsága
      if (hasUserManagementPermission) {
        items.push({ text: 'Szerepek és felhasználók kezelése', icon: <SupervisorAccount />, key: 'usermanagement' });
      }
      
      items.push({ text: 'Kijelentkezés', icon: <Logout />, key: 'logout' });
      
      return items;
    }
  };

  const authMenuItems = getMenuItems();
  
  // Dinamikus színezés a current view alapján
  const getBackgroundColor = () => {
    if (currentView === 'usermanagement') {
      return '#ff8c42'; // Narancssárga a szerepek és felhasználók kezelésénél
    } else {
      return '#3a4651'; // Ugyanaz a szín, mint a bal oldali sidebar (dark blue-gray)
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        style: {
          zIndex: currentView === 'usermanagement' ? 1300 : 1200, // Magasabb z-index a szerepek oldalon
        }
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: AUTH_SIDEBAR_WIDTH,
          backgroundColor: getBackgroundColor(),
          color: 'white',
          height: '100vh', // Teljes magasság - kitakarja a teljes képernyőt
          marginTop: 0, // Nincs margin, hogy a tetejétől induljon
        },
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h6">
          Felhasználói menü
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>
      
      <Box sx={{ overflow: 'auto', pt: 1 }}>
        <List>
          {authMenuItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                onClick={() => {
                  onSelectAuth(item.key as 'profile' | 'logout' | 'usermanagement' | 'backtomain');
                  onClose();
                }}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default MainPageSideBar;
